/**
 * RecurringEditModal Component
 *
 * Modal for editing recurring transaction templates.
 * Only edits template-level fields that affect all future transactions.
 *
 * Template-level editable fields:
 * - description, amount, category_id, cost_center_id, notes
 * - recurrence_pattern, recurrence_interval, recurrence_count, recurrence_end_date
 *
 * Non-editable (child-specific):
 * - date, due_date, payment_date, competence_date
 */

import { useState, useEffect } from "react";
import { Loader2, Save, Info, AlertCircle } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useCategories } from "@/hooks/useCategories";
import { useCostCenters } from "@/hooks/useCostCenters";
import { useRecurringEdit } from "@/hooks/useRecurringEdit";
import type { RecurringTransaction } from "@/hooks/useRecurringTransactions";
import type { RecurrencePattern } from "@/types/transaction";
import { toast } from "sonner";

// Form schema matching the database structure
const recurringEditSchema = z.object({
  description: z.string().min(1, "Descrição é obrigatória"),
  amount: z.number().min(0.01, "Valor deve ser positivo"),
  category_id: z.number().positive("Categoria é obrigatória"),
  cost_center_id: z.number().optional(),
  notes: z.string().optional(),
  recurrence_pattern: z.enum(["daily", "weekly", "monthly", "yearly"]),
  recurrence_interval: z.number().positive().int(),
  recurrence_count: z.number().positive().int().optional(),
  recurrence_end_date: z.string().optional(),
});

type RecurringEditFormData = z.infer<typeof recurringEditSchema>;

interface RecurringEditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transaction: RecurringTransaction | null;
  onSuccess?: () => void;
}

const patternTypeLabels: Record<RecurrencePattern, string> = {
  daily: "Diário",
  weekly: "Semanal",
  monthly: "Mensal",
  yearly: "Anual"
};

export function RecurringEditModal({
  open,
  onOpenChange,
  transaction,
  onSuccess,
}: RecurringEditModalProps) {
  const { categories, loading: categoriesLoading } = useCategories();
  const { costCenters, isLoading: costCentersLoading } = useCostCenters();
  const { updateRecurring, isUpdating } = useRecurringEdit();

  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<RecurringEditFormData>({
    resolver: zodResolver(recurringEditSchema),
    defaultValues: {
      description: "",
      amount: 0,
      category_id: 0,
      cost_center_id: undefined,
      notes: "",
      recurrence_pattern: "monthly" as RecurrencePattern,
      recurrence_interval: 1,
      recurrence_count: undefined,
      recurrence_end_date: "",
    },
  });

  // Reset form when transaction changes
  useEffect(() => {
    if (transaction) {
      form.reset({
        description: transaction.description || "",
        amount: transaction.amount || 0,
        category_id: transaction.category_id || 0,
        cost_center_id: transaction.cost_center_id,
        notes: transaction.notes || "",
        recurrence_pattern: transaction.recurrence_pattern || "monthly",
        recurrence_interval: transaction.recurrence_interval || 1,
        recurrence_count: transaction.recurrence_count,
        recurrence_end_date: transaction.recurrence_end_date
          ? new Date(transaction.recurrence_end_date).toISOString().split('T')[0]
          : "",
      });
      setSubmitError(null);
    }
  }, [transaction, form]);

  const handleSubmit = async (data: RecurringEditFormData) => {
    if (!transaction) return;

    setSubmitError(null);

    try {
      await updateRecurring.mutateAsync({
        id: transaction.id,
        formData: {
          description: data.description,
          amount: data.amount,
          category_id: data.category_id,
          cost_center_id: data.cost_center_id,
          notes: data.notes,
          recurrence_pattern: data.recurrence_pattern,
          recurrence_interval: data.recurrence_interval,
          recurrence_count: data.recurrence_count,
          recurrence_end_date: data.recurrence_end_date
            ? new Date(data.recurrence_end_date)
            : undefined,
        },
      });

      onSuccess?.();
    } catch (error) {
      const errorMessage = error instanceof Error
        ? error.message
        : "Erro ao atualizar transação recorrente";
      setSubmitError(errorMessage);
    }
  };

  const handleCancel = () => {
    form.reset();
    setSubmitError(null);
    onOpenChange(false);
  };

  if (!transaction) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            Editar Transação Recorrente
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Altere os campos abaixo. As alterações afetarão todas as futuras transações geradas.
          </DialogDescription>
        </DialogHeader>

        {submitError && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{submitError}</AlertDescription>
          </Alert>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            {/* Basic Information Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground">
                Informações Básicas
              </h3>

              {/* Description */}
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Descrição*</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Ex: Aluguel, Assinatura Netflix..."
                        {...field}
                        className="focus-visible:ring-primary"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Amount and Category */}
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="amount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Valor*</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                            R$
                          </span>
                          <Input
                            type="number"
                            step="0.01"
                            min="0.01"
                            placeholder="0,00"
                            className="pl-10 focus-visible:ring-primary"
                            {...field}
                            onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="category_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Categoria*</FormLabel>
                      <Select
                        onValueChange={(value) => field.onChange(parseInt(value, 10))}
                        value={field.value?.toString()}
                        disabled={categoriesLoading}
                      >
                        <FormControl>
                          <SelectTrigger className="focus-visible:ring-primary">
                            <SelectValue placeholder={categoriesLoading ? "Carregando..." : "Selecione"} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {categories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id.toString()}>
                              {cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Cost Center */}
              <FormField
                control={form.control}
                name="cost_center_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Centro de Custo</FormLabel>
                    <Select
                      onValueChange={(value) => field.onChange(value === "none" ? undefined : parseInt(value, 10))}
                      value={field.value?.toString() || "none"}
                      disabled={costCentersLoading}
                    >
                      <FormControl>
                        <SelectTrigger className="focus-visible:ring-primary">
                          <SelectValue placeholder={costCentersLoading ? "Carregando..." : "Selecione (opcional)"} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">Nenhum</SelectItem>
                        {costCenters.map((cc) => (
                          <SelectItem key={cc.id} value={cc.id.toString()}>
                            {cc.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Notes */}
              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Observações</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Informações adicionais..."
                        className="focus-visible:ring-primary resize-none"
                        rows={3}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Recurrence Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground">
                Recorrência
              </h3>

              {/* Pattern Type and Interval */}
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="recurrence_pattern"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tipo*</FormLabel>
                      <Select
                        onValueChange={(value) => field.onChange(value as RecurrencePattern)}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger className="focus-visible:ring-primary">
                            <SelectValue placeholder="Selecione" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {(Object.entries(patternTypeLabels) as [RecurrencePattern, string][]).map(([value, label]) => (
                            <SelectItem key={value} value={value}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="recurrence_interval"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Intervalo*</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="1"
                          className="focus-visible:ring-primary"
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Count and End Date */}
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="recurrence_count"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nº de Ocorrências</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="1"
                          placeholder="Ilimitado"
                          className="focus-visible:ring-primary"
                          {...field}
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="recurrence_end_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Data Final</FormLabel>
                      <FormControl>
                        <Input
                          type="date"
                          className="focus-visible:ring-primary"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Info Box */}
            <div className="flex items-start gap-3 p-3 rounded-md bg-primary/10 dark:bg-primary/20 border border-primary/20">
              <Info className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
              <div className="text-xs text-primary space-y-1">
                <p className="font-medium">Esta edição afetará todas as futuras transações</p>
                <p>geradas a partir desta recorrência.</p>
              </div>
            </div>

            {/* Footer */}
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isUpdating}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isUpdating || !form.formState.isValid}
              >
                {isUpdating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Salvar
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
